/* JMT cart drawer: Shopify Cart AJAX API + Section Rendering API. One request both mutates the cart
   and returns the re-rendered drawer HTML. */
(() => {
  'use strict';

  const jmt = (window.jmt = window.jmt || {});
  const dialog = document.getElementById('jmt-cart-drawer');
  if (!dialog || !jmt.utils) return;

  const SECTION = 'jmt-cart-drawer';
  const { debounce } = jmt.utils;
  const headers = { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
  const sectionsUrl = () => window.location.pathname;

  const showError = (message) => {
    const el = dialog.querySelector('[data-jmt-cart-error]');
    if (!el) return;
    el.textContent = message || '';
    el.hidden = !message;
  };

  /* Remember which control had focus so it can be restored after the drawer re-renders. */
  const focusSelector = () => {
    const el = document.activeElement;
    if (!el || !dialog.contains(el)) return null;
    if (el.matches('[data-jmt-qty]')) return `[data-jmt-qty="${el.dataset.jmtQty}"][data-line="${el.dataset.line}"]`;
    if (el.matches('[data-jmt-qty-input]')) return `[data-jmt-qty-input][data-line="${el.dataset.line}"]`;
    if (el.matches('[data-jmt-gift-wrap]')) return '[data-jmt-gift-wrap]';
    return null;
  };

  const render = (html) => {
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-jmt-cart-content]');
    const current = dialog.querySelector('[data-jmt-cart-content]');
    if (!next || !current) return;
    const selector = focusSelector();
    const scroller = current.querySelector('.jmt-cart__scroll');
    const scrollTop = scroller ? scroller.scrollTop : 0;
    const detailsOpen = current.querySelector('.jmt-cart__gift')?.open;
    /* Keep a gift message that is being typed (value, focus and caret) across the re-render. */
    const field = current.querySelector('[data-jmt-gift-message]');
    const typing = field && document.activeElement === field ? { value: field.value, start: field.selectionStart, end: field.selectionEnd } : null;
    current.replaceWith(next);
    const nextField = next.querySelector('[data-jmt-gift-message]');
    if (typing && nextField) {
      nextField.value = typing.value;
      nextField.focus({ preventScroll: true });
      nextField.setSelectionRange(typing.start, typing.end);
    }
    const nextScroller = next.querySelector('.jmt-cart__scroll');
    if (nextScroller) nextScroller.scrollTop = scrollTop;
    const gift = next.querySelector('.jmt-cart__gift');
    if (gift && detailsOpen) gift.open = true;
    const count = next.dataset.itemCount || '0';
    document.querySelectorAll('[data-jmt-cart-count]').forEach((badge) => {
      badge.textContent = count;
      badge.hidden = count === '0';
    });
    const target = (selector && next.querySelector(selector)) || next.querySelector('[data-jmt-close]');
    if (target && dialog.open && !typing) target.focus({ preventScroll: true });
  };

  const request = async (path, body) => {
    dialog.setAttribute('aria-busy', 'true');
    showError('');
    try {
      const response = await fetch(path, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ...body, sections: SECTION, sections_url: sectionsUrl() }),
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
      }
      if (data.sections && data.sections[SECTION]) render(data.sections[SECTION]);
      else await refresh();
      return data;
    } catch (error) {
      /* Re-sync from the server so rejected quantities revert to what is really in the cart, then explain why. */
      await refresh().catch(() => {});
      showError(error.message);
      throw error;
    } finally {
      dialog.removeAttribute('aria-busy');
    }
  };

  const refresh = async () => {
    const response = await fetch(`${sectionsUrl()}?sections=${SECTION}`, { headers: { Accept: 'application/json' } });
    const data = await response.json();
    if (data[SECTION]) render(data[SECTION]);
  };

  const add = async (items, { open = true } = {}) => {
    const data = await request('/cart/add.js', { items: Array.isArray(items) ? items : [items] });
    if (open) jmt.drawer.open(SECTION);
    document.dispatchEvent(new CustomEvent('jmt:cart:updated', { detail: data }));
    return data;
  };
  const change = (line, quantity) => request('/cart/change.js', { line, quantity });
  const noop = () => {};

  jmt.cart = { add, change, refresh, open: () => jmt.drawer.open(SECTION) };

  dialog.addEventListener('click', (event) => {
    const step = event.target.closest('[data-jmt-qty]');
    if (step) {
      const line = Number(step.dataset.line);
      const input = dialog.querySelector(`[data-jmt-qty-input][data-line="${line}"]`);
      change(line, Math.max(0, (Number(input.value) || 0) + Number(step.dataset.jmtQty))).catch(noop);
      return;
    }
    const remove = event.target.closest('[data-jmt-remove]');
    if (remove) {
      change(Number(remove.dataset.line), 0).catch(noop);
      return;
    }
    const quickAdd = event.target.closest('[data-jmt-add]');
    if (quickAdd) add({ id: Number(quickAdd.dataset.jmtAdd), quantity: 1 }, { open: false }).catch(noop);
  });

  dialog.addEventListener('change', (event) => {
    const input = event.target.closest('[data-jmt-qty-input]');
    if (input) {
      change(Number(input.dataset.line), Math.max(0, parseInt(input.value, 10) || 0)).catch(noop);
      return;
    }
    const gift = event.target.closest('[data-jmt-gift-wrap]');
    if (gift) {
      const request$ = gift.checked
        ? add({ id: Number(gift.dataset.variant), quantity: 1, properties: { _gift_wrap: 'true' } }, { open: false })
        : change(Number(gift.dataset.line), 0);
      request$.catch(() => {
        gift.checked = !gift.checked;
      });
    }
  });

  /* Quick add from product cards outside the drawer: add, then open the drawer. */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-jmt-add]');
    if (!button || dialog.contains(button)) return;
    button.disabled = true;
    add({ id: Number(button.dataset.jmtAdd), quantity: 1 })
      .catch(noop)
      .finally(() => {
        button.disabled = false;
      });
  });

  /* Gift message: saved quietly (no re-render, so typing is never interrupted). It also posts with the checkout form. */
  const saveMessage = debounce((value) => {
    fetch('/cart/update.js', { method: 'POST', headers, body: JSON.stringify({ attributes: { 'Gift message': value } }) }).catch(noop);
  }, 600);
  dialog.addEventListener('input', (event) => {
    const field = event.target.closest('[data-jmt-gift-message]');
    if (field) saveMessage(field.value);
  });

  /* Quantity fields commit on change; Enter must not submit the checkout form. */
  dialog.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' && event.target.matches('[data-jmt-qty-input]')) {
      event.preventDefault();
      event.target.blur();
    }
  });

  /* Hooks for Phase 2 product forms: <form data-jmt-product-form> or a jmt:cart:add event. */
  document.addEventListener('submit', (event) => {
    const form = event.target.closest && event.target.closest('form[data-jmt-product-form]');
    if (!form) return;
    event.preventDefault();
    const data = new FormData(form);
    add({ id: Number(data.get('id')), quantity: Number(data.get('quantity')) || 1 }).catch(noop);
  });
  document.addEventListener('jmt:cart:add', (event) => {
    add(event.detail.items || event.detail).catch(noop);
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refresh().catch(noop);
  });
})();
